import { ReactElement, useContext, useEffect, useState } from 'react';
import debounce from 'lodash-es/debounce';

import { getSearchResults, SearchModifier, SearchParameters } from '@gn-audio/jss-core';

import { SearchMenuFields } from '../../../types';
import { useFallbackTranslation } from '../../../utils/hooks';
import { DISPLAY_VERSIONS } from '../../MegaMenu';
import { SearchContext } from '../providers';
import { transformSearchResultsWithEOL } from '../utils';
import { PopularProducts } from './PopularProducts';
import { ProductResults } from './ProductResults';
import { QuickLinksView } from './QuickLinksView';
import { SearchMenuInput } from './SearchMenuInput';
import { useSearchTracking } from './SearchTracking';

type SearchMenuProps = {
  menuItem: SearchMenuFields;
  isMenuOpened?: boolean;
};

export const SearchMenu = ({ menuItem, isMenuOpened = false }: SearchMenuProps): ReactElement => {
  const { searchResults, setSearchResults, searchFilter } = useContext(SearchContext);
  const { locale: getLocale, t } = useFallbackTranslation();
  const { trackSearchEvent } = useSearchTracking();
  const [searchQuery, setSearchQuery] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const { popularSearches, searchTextPlaceholder } = menuItem;
  const numberOfResultsPerPage = menuItem.numberOfResultsPerPage?.value ?? 4;

  useEffect(() => {
    const debouncedHandleSearch = debounce(async () => {
      setIsLoading(true);

      const searchParams: SearchParameters = {
        productSearchFilter: searchFilter,
        searchQuery,
        skip: 0,
        searchModifier: SearchModifier.Prioritize,
        numberOfResultsPerPage,
        locale: getLocale(),
        filterLowStateWithoutSupportUrl: true,
      };
      const result = await getSearchResults(searchParams);
      const resultEOLFiltered = transformSearchResultsWithEOL(result);
      setIsLoading(false);

      trackSearchEvent({
        search_term: searchQuery,
        search_results_count: resultEOLFiltered.count,
        search_type: 'live_search',
        search_source: 'desktop',
      });

      if (setSearchResults) {
        setSearchResults(resultEOLFiltered);
      }
    }, 500);

    if (searchQuery !== '') {
      debouncedHandleSearch();
    } else {
      if (setSearchResults) {
        setSearchResults({ count: 0, items: [] });
        setIsLoading(false);
      }
    }

    return () => {
      debouncedHandleSearch.cancel();
    };
  }, [
    searchQuery,
    setSearchResults,
    numberOfResultsPerPage,
    getLocale,
    trackSearchEvent,
    searchFilter,
  ]);

  return (
    <div className={DISPLAY_VERSIONS.DESKTOP}>
      <div
        aria-label={t('topmenu.SearchPage')}
        className={`search-container flex w-full flex-col ${
          isMenuOpened
            ? 'absolute bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] lg:block'
            : ' '
        }bg-white px-2`}
      >
        <div className="mb-12 mt-8 flex items-center justify-center">
          <div className="w-96">
            <SearchMenuInput
              busy={isLoading}
              inputPlaceholderText={searchTextPlaceholder?.value || ''}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>
        </div>
        {searchResults && searchResults.count > 0 ? (
          <ProductResults searchQuery={searchQuery} menuItem={menuItem} />
        ) : (
          <>
            <>
              {searchResults && searchResults.items.length === 0 ? (
                <div className="flex w-full items-center justify-center">
                  <span>{searchResults.error ? t('cart.ExceptionDefaultText') : ''}</span>
                </div>
              ) : null}
            </>
            <div className="flex justify-center">
              <div className="flex w-96 flex-col items-center justify-center xs:w-auto">
                <PopularProducts fields={popularSearches} />
                <QuickLinksView quickLinks={menuItem.quickLinks} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
